import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import {
  type Chain,
  type HttpTransport,
  parseUnits,
  type PrivateKeyAccount,
  type TypedDataParameter,
  type WalletClient,
} from "viem";

import { errorMessages } from "#/config/errorMessages";
import { type ChainId } from "#/config/web3/contractAddresses";
import { useWalletClient } from "#/hooks/useWalletClient";
import {
  type ComboTrade,
  type ComboTradeRequest,
  type ComboTradeResponse,
} from "#/types/orders";
import { type KyanError } from "#/types/service";
import { getPublicClient } from "#/utils/getPublicClient";
import { getSignatureDeadline } from "#/utils/getSignatureDeadline";
import { getDirectionIndex } from "#/utils/marketActionConverter";
import { EIP712Domain } from "#/utils/signature";

import { postComboOrder } from "../kyan.service";
import {
  comboOrderErrorHandler,
  defaultApiResponseHandler,
} from "../kyan.utils";
import { useTradingSession } from "./useTradingSession";

export const UserComboOrder: TypedDataParameter[] = [
  { name: "deadline", type: "uint256" },
  { name: "marketOrders", type: "OrderTyped[]" },
  { name: "limitNetPrice", type: "int256" },
  { name: "limitPerpPrice", type: "int256" },
  { name: "taker", type: "address" },
];

export const OrderTyped: TypedDataParameter[] = [
  { name: "instrumentName", type: "string" },
  { name: "size", type: "uint256" },
  { name: "direction", type: "uint8" },
];

const buildComboOrderTypedData = (
  trade: ComboTrade | ComboTradeRequest,
  deadline: number,
  chainId: ChainId,
) => {
  const marketOrders = trade.market_orders.map((order) => ({
    instrumentName: order.instrument_name,
    direction: getDirectionIndex(order.direction),
    size: parseUnits((order.contracts ?? order.amount ?? 0).toString(), 6),
  }));
  return {
    domain: EIP712Domain[chainId],
    types: { OrderTyped, UserComboOrder },
    primaryType: "UserComboOrder" as const,
    message: {
      deadline,
      marketOrders,
      taker: trade.taker,
      limitNetPrice: parseUnits(trade.limit_total_net_premium.toString(), 6),
      limitPerpPrice: parseUnits((trade.limit_perp_price ?? 0).toString(), 6),
    },
  };
};

const signComboOrder = async (
  trade: ComboTrade,
  walletClient: WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
): Promise<ComboTradeRequest> => {
  try {
    const account = walletClient.account.address;
    const signature_deadline = getSignatureDeadline();

    const typedData = buildComboOrderTypedData(
      trade,
      signature_deadline,
      walletClient.chain?.id as ChainId,
    );

    const signature = await walletClient.signTypedData({
      account,
      ...typedData,
    });

    return {
      ...trade,
      signature_deadline,
      signature,
    };
  } catch (err) {
    console.error("Error signing combo order:", err);
    if (String(err).includes("chainId should be same as current chainId")) {
      throw new Error(
        `Wrong chainId on signature: ${await walletClient.getChainId()}`,
      );
    }
    if (String(err).includes("UserRejectedRequestError")) {
      throw new Error("Combo order signature rejected");
    }

    throw new Error("Error signing market order");
  }
};

const validateComboOrderSignatures = async (
  trade: ComboTradeRequest,
  chainId: ChainId,
): Promise<boolean> => {
  const publicClient = getPublicClient(chainId);

  const typedData = buildComboOrderTypedData(
    trade,
    trade.signature_deadline,
    chainId,
  );

  return await publicClient.verifyTypedData({
    address: trade.taker,
    signature: trade.signature,
    ...typedData,
  });
};

export const usePostComboOrder = (
  options?: UseMutationOptions<unknown, Error, ComboTrade>,
) => {
  const { walletClient } = useWalletClient();
  const { shouldUseSessionHash } = useTradingSession();

  return useMutation({
    ...options,
    mutationFn: async (orders: ComboTrade) => {
      if (!walletClient) {
        throw new Error(errorMessages.WALLET_CLIENT_UNAVAILABLE);
      }

      const serviceName = "Post Combo Order";
      const eventHandlers = {
        onSuccess: (response: ComboTradeResponse) => {
          comboOrderErrorHandler({ serviceName, response });
        },
        onError: (error: KyanError) => {
          comboOrderErrorHandler({ serviceName, error });
        },
      };
      if (shouldUseSessionHash) {
        return defaultApiResponseHandler({
          serviceName,
          payload: { data: orders, includeSessionHash: true },
          service: postComboOrder,
          ...eventHandlers,
        });
      }

      const signedOrders = await signComboOrder(
        orders,
        walletClient as WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
      );

      const isSignatureValid = await validateComboOrderSignatures(
        signedOrders,
        walletClient.chain?.id as ChainId,
      );

      const response = await defaultApiResponseHandler({
        serviceName,
        payload: { data: signedOrders },
        service: postComboOrder,
        validation: [isSignatureValid],
        ...eventHandlers,
      });

      return response;
    },
  });
};
